import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Button,
  Input,
  Offcanvas,
  OffcanvasHeader,
  OffcanvasBody,
  Card,
  CardBody,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Label,
} from "reactstrap";
import { Triangle } from "react-loader-spinner";
import toast from "react-hot-toast";
import DataTable from "react-data-table-component";
import StatusUpdateModal from "../component/StatusUpdateModal";
import OrderServices from "../../../services/OrderingServices/OrderServices";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import CancelModal from "../component/CancelModal";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/flatpickr.min.css";

const customStyles = {
  headCells: {
    style: {
      fontSize: "0.92rem",
      fontWeight: 610,
    },
  },
  rows: {
    style: {
      fontSize: "0.9rem",
    },
  },
};

const OrderDetailsList = () => {
  const navigate = useNavigate();
  const [itemList, setItemList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalRows, setTotalRows] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  const selectedTable = JSON.parse(localStorage.getItem("selectedTable"));
  const [showOffcanvas, setShowOffcanvas] = useState(false);

  // Modals
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  // Filters
  const [filters, setFilters] = useState({
    refNumber: "",
    productName: "",
    startDate: "",
    endDate: "",
  });
  const [debouncedFilters, setDebouncedFilters] = useState(filters);

  useEffect(() => {
    const today = new Date();
    const lastMonth = new Date();
    lastMonth.setMonth(today.getMonth() - 1);

    setFilters((prev) => ({
      ...prev,
      startDate: lastMonth.toISOString().split("T")[0],
      endDate: today.toISOString().split("T")[0],
    }));
  }, []);

  // Status & Payment Badge Helpers
  const getStatusBadge = (status) => {
    const statusStyles = {
      Ordered: "bg-warning",
      Preparing: "bg-secondary",
      Ready: "bg-primary",
      Completed: "bg-success",
      Cancelled: "bg-danger",
    };
    return (
      <span className={`badge ${statusStyles[status] || "bg-primary"}`}>
        {status || "No Data"}
      </span>
    );
  };

  const getPaymentBadge = (e) => {
    const statusStyles = {
      Paid: "bg-success",
      Pending: "bg-warning",
      Failed: "bg-danger",
      Processing: "bg-secondary",
      Refunded: "bg-primary",
      PartiallyRefunded: "bg-secondary",
      OrderCancelled: "bg-danger",
      Completed: "bg-success",
      Chargeback: "bg-secondary",
    };
    return (
      <span className={`badge ${statusStyles[e] || "bg-danger"}`}>
        {e || "No Data"}
      </span>
    );
  };

  // Fetch Orders
  const fetchOrderList = async (
    page = currentPage,
    size = perPage,
    currentFilters = debouncedFilters
  ) => {
    try {
      setLoading(true);
      const fromDate = currentFilters.startDate
        ? `${currentFilters.startDate}T00:00:00`
        : "";
      const toDate = currentFilters.endDate
        ? `${currentFilters.endDate}T23:59:59`
        : "";

      const response = await OrderServices.getOrderDetailList({
        pageNumber: page,
        pageSize: size,
        refNumber: currentFilters.refNumber,
        fromDate,
        toDate,
        productName: currentFilters.productName,
      });

      const orderDetails = response.items || [];
      const formattedData = orderDetails.map((item, index) => ({
        sn: (page - 1) * size + index + 1,
        id: item?.orderId || index,
        productId: item?.productId || 0,
        productName: item?.productName || "string",
        productPrice: item?.productPrice || 0,
        quantity: item?.quantity || 0,
        complimentaryQuantity: item?.complimentaryQuantity || 0,
        finalQuantity: item?.finalQuantity || 0,
        totalPrice: item?.totalPrice || 0,
        refNumber: item?.refNumber || "string",
        orderStatus: item?.orderStatus || "Ordered",
      }));

      setItemList(formattedData);
      setTotalRows(response.totalCount || 0);
    } catch (error) {
      console.error("Error fetching Order Details:", error);
      setItemList([]);
      setTotalRows(0);
    } finally {
      setLoading(false);
    }
  };

  // Debounce filters
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedFilters(filters);
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [filters]);

  useEffect(() => {
    if (
      debouncedFilters.startDate &&
      debouncedFilters.endDate &&
      debouncedFilters.startDate > debouncedFilters.endDate
    ) {
      toast.error("Start date cannot be after end date");
      return;
    }
    fetchOrderList(currentPage, perPage);
  }, [currentPage, perPage, debouncedFilters]);

  const handlePageChange = (page) => setCurrentPage(page);
  const handlePerRowsChange = (newPerPage, page) => {
    setPerPage(newPerPage);
    setCurrentPage(page);
  };

  // Cancel Modal
  const toggleCancelModal = (item = null) => {
    setSelectedItem(item);
    setIsCancelModalOpen(!isCancelModalOpen);
  };

  const handleCancel = async () => {
    try {
      if (!selectedItem) return;
      await OrderServices.cancelOrder(selectedItem.id);
      fetchOrderList();
      setIsCancelModalOpen(false);
      toast.success("Order cancelled successfully");
    } catch (error) {
      console.error(error);
      toast.error("Failed to cancel order");
    }
  };

  // Filter Handlers
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleDateChange = (dates, name) => {
    if (!dates || !dates.length) return;
    const formattedDate = dates[0].toISOString().split("T")[0];
    setFilters((prev) => ({ ...prev, [name]: formattedDate }));
  };

  const columns = [
    { name: "S.N", selector: (row) => row.sn, sortable: true, width: "75px" },
    {
      name: "Reference No",
      selector: (row) => (
        <Link to={`/order/${row.refNumber}`}>{row.refNumber}</Link>
      ),
      wrap: true,
      sortable: true,
      width: "14vw",
    },
    {
      name: "Name",
      selector: (row) => row.productName,
      sortable: true,
      wrap: true,
    },
    {
      name: "Quantity",
      selector: (row) => row.finalQuantity,
      sortable: true,
      center: true,
    },
    {
      name: "Price",
      selector: (row) => row.productPrice,
      sortable: true,
      right: true,
    },
    {
      name: "Total Price",
      selector: (row) => row.totalPrice,
      sortable: true,
      right: true,
    },
  ];

  return (
    <Container fluid>
      <div className="page-content">
        <BreadCrumb title="Order Details List" pageTitle="Order Details" />

        {/* Mobile Filter Button */}
        <div className="d-lg-none d-md-none d-block mb-2">
          <Button color="success" onClick={() => setShowOffcanvas(true)}>
            <i className="bx bx-filter-alt"></i>
          </Button>
        </div>

        {/* Desktop Filters */}
        <Row className="gap-0 mb-3 d-lg-flex d-none">
          <Col xs={12} lg={3} className="mb-2">
            <Flatpickr
              value={filters.startDate}
              options={{ dateFormat: "Y-m-d" }}
              onChange={(dates) => handleDateChange(dates, "startDate")}
              placeholder="Start Date"
              className="form-control"
            />
          </Col>
          <Col xs={12} lg={3} className="mb-2">
            <Flatpickr
              value={filters.endDate}
              options={{ dateFormat: "Y-m-d", minDate: filters.startDate }}
              onChange={(dates) => handleDateChange(dates, "endDate")}
              placeholder="End Date"
              className="form-control"
            />
          </Col>
          <Col xs={12} lg={3} className="mb-2">
            <Input
              type="text"
              name="refNumber"
              value={filters.refNumber}
              onChange={handleFilterChange}
              placeholder="Search by reference number"
            />
          </Col>
          <Col xs={12} lg={3} className="mb-2">
            <Input
              type="text"
              name="productName"
              value={filters.productName}
              onChange={handleFilterChange}
              placeholder="Search by product name"
            />
          </Col>
        </Row>

        {/* Mobile Offcanvas Filters */}
        <Offcanvas
          isOpen={showOffcanvas}
          toggle={() => setShowOffcanvas(!showOffcanvas)}
          direction="bottom"
          className="d-lg-none"
          style={{
            height: "80vh",
            borderTopLeftRadius: "1rem",
            borderTopRightRadius: "1rem",
            overflowY: "auto",
          }}
        >
          <OffcanvasHeader toggle={() => setShowOffcanvas(!showOffcanvas)}>
            <h3 className="text-lg font-semibold">Filters</h3>
          </OffcanvasHeader>
          <OffcanvasBody>
            <div className="mb-2">
              <Label>Start Date</Label>
              <Input
                type="date"
                name="startDate"
                value={filters.startDate}
                onChange={handleFilterChange}
              />
            </div>
            <div className="mb-2">
              <Label>End Date</Label>
              <Input
                type="date"
                name="endDate"
                value={filters.endDate}
                onChange={handleFilterChange}
              />
            </div>
            <div className="mb-2">
              <Label>Reference Number</Label>
              <Input
                type="text"
                name="refNumber"
                value={filters.refNumber}
                onChange={handleFilterChange}
              />
            </div>
            <div className="mb-2">
              <Label>Product Name</Label>
              <Input
                type="text"
                name="productName"
                value={filters.productName}
                onChange={handleFilterChange}
              />
            </div>
          </OffcanvasBody>
          <div className="px-4 py-2 d-flex justify-content-end sticky-bottom bg-white border-top">
            <Button color="success" onClick={() => setShowOffcanvas(false)}>
              Apply Filters
            </Button>
          </div>
        </Offcanvas>

        {/* Desktop Table */}
        <div className="d-lg-block d-none">
          <DataTable
            responsive
            striped
            pagination
            fixedHeader
            highlightOnHover
            pointerOnHover
            persistTableHead
            progressPending={loading}
            customStyles={customStyles}
            columns={columns}
            data={itemList}
            paginationServer
            paginationTotalRows={totalRows}
            onChangeRowsPerPage={handlePerRowsChange}
            onChangePage={handlePageChange}
            paginationDefaultPage={currentPage}
            paginationPerPage={perPage}
            progressComponent={
              <div className="my-3">
                <Triangle
                  visible={true}
                  height="80"
                  width="80"
                  color="#5B71B9"
                  ariaLabel="triangle-loading"
                />
                <h5 className="mt-1">Loading...</h5>
              </div>
            }
          />
        </div>

        {/* Mobile Cards */}
        <div className = "d-block d-lg-none">
        {loading ? (
          <div className="my-5 d-flex justify-content-center">
            <Triangle visible={true} height="60" width="60" color="#5B71B9" />
          </div>
        ) : (
          <>
            {itemList.map((item, index) => (
              <Card
                key={item.id || index}
                className="mb-3 shadow-sm d-block d-lg-none mt-2"
              >
                {/* <CardBody> */}
                {/* <Row> */}
                <div
                  key={item.sn}
                  className="order-card p-3 mb-3 shadow-sm rounded"
                >
                  {/* Card Header */}
                  <div className="order-card-header d-flex justify-content-between align-items-center mb-2">
                    <strong className="text-bold">{item.productName}</strong>
                    <span className="ref text-muted">
                      <Link
                        to={`/order/${item.refNumber}`}
                        className="text-decoration-none"
                      >
                        {item.refNumber}
                      </Link>
                    </span>
                  </div>

                  {/* Card Body */}
                  <div className="order-card-body">
                    <div className="d-flex justify-content-between mb-1">
                      <span className="text-muted">Quantity:</span>
                      <span>{item.finalQuantity || "N/A"}</span>
                    </div>
                    <div className="d-flex justify-content-between mb-1">
                      <span className="text-muted">Price:</span>
                      <span>Rs. {item.productPrice || "N/A"}</span>
                    </div>
                    <div className="d-flex justify-content-between">
                      <span className="text-muted">Total:</span>
                      <span className="fw-bold text-success">
                        Rs. {item.totalPrice || "N/A"}
                      </span>
                    </div>
                  </div>
                </div>
              </Card>
            ))}

            {/* Pagination for Mobile View */}
            {/* Mobile Cards */}

            {loading ? (
              <div className="my-5 d-flex justify-content-center">
                <Triangle
                  visible={true}
                  height="60"
                  width="60"
                  color="#5B71B9"
                />
              </div>
            ) : (
              <>
                {itemList.map((item, index) => (
                  <Card
                    key={item.id || index}
                    className="mb-3 shadow-sm d-block d-lg-none mt-2"
                  >
                    <div className="order-card p-3 mb-3 shadow-sm rounded">
                      <div className="order-card-header d-flex justify-content-between align-items-center mb-2">
                        <strong className="text-bold">
                          {item.productName}
                        </strong>
                        <span className="ref text-muted">
                          <Link
                            to={`/order/${item.refNumber}`}
                            className="text-decoration-none"
                          >
                            {item.refNumber}
                          </Link>
                        </span>
                      </div>
                      <div className="order-card-body">
                        <div className="d-flex justify-content-between mb-1">
                          <span className="text-muted">Quantity:</span>
                          <span>{item.finalQuantity || "N/A"}</span>
                        </div>
                        <div className="d-flex justify-content-between mb-1">
                          <span className="text-muted">Price:</span>
                          <span>Rs. {item.productPrice || "N/A"}</span>
                        </div>
                        <div className="d-flex justify-content-between">
                          <span className="text-muted">Total:</span>
                          <span className="fw-bold text-success">
                            Rs. {item.totalPrice || "N/A"}
                          </span>
                        </div>
                      </div>
                    </div>
                  </Card>
                ))}

                {/* Pagination: Outside the map */}
                {itemList.length > 0 && (
                  <div className="d-flex justify-content-center mt-3 mb-3 d-lg-none">
                    <nav>
                      <ul className="pagination mb-0">
                        {/* Prev Button */}
                        <li
                          className={`page-item ${
                            currentPage === 1 ? "disabled" : ""
                          }`}
                        >
                          <button
                            className="page-link"
                            onClick={() =>
                              setCurrentPage(Math.max(currentPage - 1, 1))
                            }
                          >
                            <i className="ri-arrow-left-s-line"></i>
                          </button>
                        </li>

                        {/* Page Numbers */}
                        {(() => {
                          const totalPages = Math.ceil(totalRows / perPage);
                          const maxVisible = 5;
                          let startPage = Math.max(
                            currentPage - Math.floor(maxVisible / 2),
                            1
                          );
                          let endPage = Math.min(
                            startPage + maxVisible - 1,
                            totalPages
                          );

                          if (endPage - startPage + 1 < maxVisible) {
                            startPage = Math.max(endPage - maxVisible + 1, 1);
                          }

                          return [...Array(endPage - startPage + 1)].map(
                            (_, idx) => {
                              const page = startPage + idx;
                              return (
                                <li
                                  key={page}
                                  className={`page-item ${
                                    currentPage === page ? "active" : ""
                                  }`}
                                >
                                  <button
                                    className="page-link"
                                    onClick={() => setCurrentPage(page)}
                                  >
                                    {page}
                                  </button>
                                </li>
                              );
                            }
                          );
                        })()}

                        {/* Next Button */}
                        <li
                          className={`page-item ${
                            currentPage === Math.ceil(totalRows / perPage)
                              ? "disabled"
                              : ""
                          }`}
                        >
                          <button
                            className="page-link"
                            onClick={() =>
                              setCurrentPage(
                                Math.min(
                                  currentPage + 1,
                                  Math.ceil(totalRows / perPage)
                                )
                              )
                            }
                          >
                            <i className="ri-arrow-right-s-line"></i>
                          </button>
                        </li>
                      </ul>
                    </nav>
                  </div>
                )}
              </>
            )}
          </>
        )}
        </div>

        {/* Modals */}
        <StatusUpdateModal
          isOpen={isStatusModalOpen}
          toggle={() => setIsStatusModalOpen(!isStatusModalOpen)}
          orderDetailId={selectedItem?.refNumber}
          currentStatus={selectedItem?.orderStatus}
          fetchOrderList={fetchOrderList}
        />

        <CancelModal
          isOpen={isCancelModalOpen}
          toggle={() => setIsCancelModalOpen(!isCancelModalOpen)}
          onDeleteClick={handleCancel}
        />
      </div>
    </Container>
  );
};

export default OrderDetailsList;
